// gti-chms-frontend/src/context/ToastContext.tsx
import React, { createContext, useContext, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import Toast from '../components/common/Toast'; // Make sure this path is correct

interface ToastMessage {
    id: string;
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
    duration?: number; // Duration in ms, defaults to 5000
}

interface ToastContextType {
    showToast: (message: string, type?: 'success' | 'error' | 'info' | 'warning', duration?: number) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [toasts, setToasts] = useState<ToastMessage[]>([]);

    // Function to add a new toast message
    const showToast = useCallback((message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info', duration: number = 5000) => {
        const id = Math.random().toString(36).substr(2, 9); // Generate a unique ID
        setToasts(prevToasts => [...prevToasts, { id, message, type, duration }]);
    }, []);

    // Function to remove a toast message
    const removeToast = useCallback((id: string) => {
        setToasts(prevToasts => prevToasts.filter(toast => toast.id !== id));
    }, []);

    return (
        <ToastContext.Provider value={{ showToast }}>
            {children}
            {/* Render the Toast container fixed at the bottom right */}
            <div className="fixed bottom-4 right-4 z-[1000] flex flex-col space-y-3">
                {toasts.map(toast => (
                    <Toast key={toast.id} {...toast} onDismiss={() => removeToast(toast.id)} />
                ))}
            </div>
        </ToastContext.Provider>
    );
};

// Custom hook to easily use the toast functionality in components
export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};
